import { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, FolderOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { uploadMedia, getMedia } from '../services/mediaService';
import type { Media } from '../types/media';
import { logger } from '../lib/logger';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  className?: string;
  aspectRatio?: 'square' | '16:9' | '4:3' | 'auto';
  placeholder?: string;
}

export function ImageUpload({
  value,
  onChange,
  className = '',
  aspectRatio = 'auto',
  placeholder = 'Bild hochladen',
}: ImageUploadProps) {
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [showLibrary, setShowLibrary] = useState(false);
  const [mediaList, setMediaList] = useState<Media[]>([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const aspectRatioClass = {
    square: 'aspect-square',
    '16:9': 'aspect-video',
    '4:3': 'aspect-[4/3]',
    auto: 'min-h-[120px]',
  }[aspectRatio];

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !user) return;

      if (!file.type.startsWith('image/')) {
        setError('Bitte wähle ein Bild aus');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setError('Datei zu groß (max. 10MB)');
        return;
      }

      setError('');
      setIsUploading(true);

      try {
        const media = await uploadMedia(user.id, file);
        onChange(media.url);
      } catch (err) {
        logger.error('Upload error:', err);
        setError('Upload fehlgeschlagen');
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [user, onChange]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (!file || !user) return;

      if (!file.type.startsWith('image/')) {
        setError('Bitte wähle ein Bild aus');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setError('Datei zu groß (max. 10MB)');
        return;
      }

      setError('');
      setIsUploading(true);

      try {
        const media = await uploadMedia(user.id, file);
        onChange(media.url);
      } catch (err) {
        logger.error('Upload error:', err);
        setError('Upload fehlgeschlagen');
      } finally {
        setIsUploading(false);
      }
    },
    [user, onChange]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleRemove = () => {
    onChange('');
  };

  const openLibrary = async () => {
    if (!user) return;
    setLoadingLibrary(true);
    setShowLibrary(true);

    try {
      const media = await getMedia(user.id);
      setMediaList(media);
    } catch (err) {
      logger.error('Error loading media:', err);
    } finally {
      setLoadingLibrary(false);
    }
  };

  const selectFromLibrary = (url: string) => {
    onChange(url);
    setShowLibrary(false);
  };

  if (value) {
    return (
      <div className={`relative rounded-xl overflow-hidden border border-gray-200 ${aspectRatioClass} ${className}`}>
        <img src={value} alt="" className="w-full h-full object-cover" />
        <button
          type="button"
          onClick={handleRemove}
          className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-colors"
        >
          <X className="w-4 h-4 text-gray-700" />
        </button>
        <div className="absolute bottom-2 right-2 flex gap-1">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-colors"
            title="Neues Bild hochladen"
          >
            <Upload className="w-4 h-4 text-gray-700" />
          </button>
          <button
            type="button"
            onClick={openLibrary}
            className="p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-colors"
            title="Aus Mediathek wählen"
          >
            <FolderOpen className="w-4 h-4 text-gray-700" />
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        {showLibrary && (
          <MediaLibraryModal
            mediaList={mediaList}
            loading={loadingLibrary}
            onSelect={selectFromLibrary}
            onClose={() => setShowLibrary(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className={`relative border-2 border-dashed rounded-xl transition-colors ${aspectRatioClass} ${
          isUploading ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
          {isUploading ? (
            <>
              <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
              <p className="text-sm text-gray-500">Wird hochgeladen...</p>
            </>
          ) : (
            <>
              <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 text-center mb-3">{placeholder}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Hochladen
                </button>
                <button
                  type="button"
                  onClick={openLibrary}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <FolderOpen className="w-4 h-4" />
                  Mediathek
                </button>
              </div>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}

      {showLibrary && (
        <MediaLibraryModal
          mediaList={mediaList}
          loading={loadingLibrary}
          onSelect={selectFromLibrary}
          onClose={() => setShowLibrary(false)}
        />
      )}
    </div>
  );
}

function MediaLibraryModal({
  mediaList,
  loading,
  onSelect,
  onClose,
}: {
  mediaList: Media[];
  loading: boolean;
  onSelect: (url: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');

  const filteredMedia = search
    ? mediaList.filter(
        (m) =>
          m.filename.toLowerCase().includes(search.toLowerCase()) ||
          m.alt_text?.toLowerCase().includes(search.toLowerCase())
      )
    : mediaList;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !mt-0">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Mediathek</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen..."
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {search ? 'Keine Ergebnisse gefunden' : 'Noch keine Medien vorhanden'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {filteredMedia.map((media) => (
                <button
                  key={media.id}
                  type="button"
                  onClick={() => onSelect(media.url)}
                  className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-primary transition-colors focus:outline-none focus:border-primary"
                >
                  <img
                    src={media.url}
                    alt={media.alt_text || media.filename}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
